import React from 'react';
import { cn } from '../lib/utils';

/**
 * NeoCard - A neomorphic container
 * @param inset - Whether the card should be "pressed" into the surface
 */
export const NeoCard = ({ 
  children, 
  className = '', 
  inset = false, 
  ...props 
}: { 
  children: React.ReactNode; 
  className?: string; 
  inset?: boolean;
  [key: string]: any;
}) => {
  const baseClass = inset ? 'neo-inset' : 'neo';
  return (
    <div 
      className={cn(baseClass, 'rounded-[2rem] p-6', className)} 
      {...props}
    >
      {children}
    </div>
  );
};

/**
 * NeoButton - An interactive neomorphic button
 */
export const NeoButton = ({ 
  children, 
  className = '', 
  variant = 'default', 
  ...props 
}: { 
  children: React.ReactNode; 
  className?: string; 
  variant?: 'default' | 'primary';
  [key: string]: any;
}) => {
  const isPrimary = variant === 'primary';
  return (
    <button
      className={cn(
        'neo neo-interactive rounded-2xl px-6 py-3 font-bold transition-all',
        isPrimary 
          ? 'bg-[var(--color-primary)] text-[var(--color-primary-content)] hover:brightness-110' 
          : 'text-[var(--color-text)] hover:text-[var(--color-primary)]',
        className
      )}
      {...props}
    >
      {children}
    </button>
  );
};

/**
 * NeoInput - A neomorphic text input
 */
export const NeoInput = ({ 
  className = '', 
  icon,
  ...props 
}: { 
  className?: string;
  icon?: React.ReactNode;
  [key: string]: any;
}) => {
  return (
    <div className={cn("relative w-full group", className)}>
      {icon && (
        <div className="absolute left-5 top-1/2 -translate-y-1/2 text-muted group-focus-within:text-primary transition-colors">
          {icon}
        </div>
      )}
      <input
        className={cn(
          'w-full neo-inset bg-transparent outline-none py-4 rounded-2xl transition-all',
          icon ? 'pl-14 pr-5' : 'px-5',
          'text-[var(--color-text)] caret-[var(--color-text)] placeholder:text-[var(--color-muted)]',
          'focus:ring-2 focus:ring-[var(--color-primary)]/30',
        )}
        {...props}
      />
    </div>
  );
};
